import React from 'react';
import { Card, Typography, Button, Space, Divider } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import LoanAgreementGenerator from '../components/LoanAgreementGenerator';
import PDFService from '../utils/pdfService';

const { Title, Text } = Typography;

const TestPDFGeneration = () => {
  // Sample loan data for testing
  const sampleLoan = {
    loan_amount: 5000,
    loan_period: 30,
    loan_enddate: '2025-02-15',
    collateral: 'Toyota Vitz - Registration ABC123Z',
    disbursement_account: 'MTN Mobile Money - 0977123456',
    gps_fee: 400,
    endorsement_fee: 650,
    date_created: '2025-01-15',
  };

  const sampleBorrower = {
    first_name: 'John',
    last_name: 'Mwansa',
    national_id: '123456/78/1',
    occupation: 'Business Owner',
    address: 'Plot 123, Chilanga Road, Lusaka',
    email: 'john.mwansa@email.com',
    phone: '0977123456',
  };

  const sampleNextOfKin = {
    names: 'Mary Mwansa',
    nrc: '987654/32/1',
    occupation: 'Teacher',
    address: 'Plot 456, Roma Area, Lusaka',
    phone: '0966654321',
    relationship: 'Spouse',
  };

  const handleDirectDownload = async () => {
    try {
      const { loanData, borrowerData, nextOfKinData } = PDFService.formatLoanDataForPDF(
        sampleLoan, 
        sampleBorrower, 
        sampleNextOfKin
      );
      
      await PDFService.downloadPopulatedPDF(
        loanData, 
        borrowerData, 
        nextOfKinData,
        'sample_loan_agreement.pdf'
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={2}>PDF Generation Test Page</Title>
        <Text type="secondary">
          This page demonstrates the loan agreement PDF generation functionality.
        </Text>

        <Divider />

        <Title level={3}>Sample Data</Title>
        <div style={{ marginBottom: 24 }}>
          <Card size="small" title="Loan Details" style={{ marginBottom: 16 }}>
            <Text><strong>Amount:</strong> ZMW {sampleLoan.loan_amount.toLocaleString()}</Text><br />
            <Text><strong>Period:</strong> {sampleLoan.loan_period} days</Text><br />
            <Text><strong>End Date:</strong> {sampleLoan.loan_enddate}</Text><br />
            <Text><strong>Collateral:</strong> {sampleLoan.collateral}</Text><br />
            <Text><strong>GPS Fee:</strong> ZMW {sampleLoan.gps_fee}</Text><br />
            <Text><strong>Endorsement Fee:</strong> ZMW {sampleLoan.endorsement_fee}</Text>
          </Card>

          <Card size="small" title="Borrower Details" style={{ marginBottom: 16 }}>
            <Text><strong>Name:</strong> {sampleBorrower.first_name} {sampleBorrower.last_name}</Text><br />
            <Text><strong>NRC:</strong> {sampleBorrower.national_id}</Text><br />
            <Text><strong>Occupation:</strong> {sampleBorrower.occupation}</Text><br />
            <Text><strong>Address:</strong> {sampleBorrower.address}</Text><br />
            <Text><strong>Phone:</strong> {sampleBorrower.phone}</Text><br />
            <Text><strong>Email:</strong> {sampleBorrower.email}</Text>
          </Card>

          <Card size="small" title="Next of Kin Details" style={{ marginBottom: 16 }}>
            <Text><strong>Name:</strong> {sampleNextOfKin.names}</Text><br />
            <Text><strong>NRC:</strong> {sampleNextOfKin.nrc}</Text><br />
            <Text><strong>Occupation:</strong> {sampleNextOfKin.occupation}</Text><br />
            <Text><strong>Address:</strong> {sampleNextOfKin.address}</Text><br />
            <Text><strong>Phone:</strong> {sampleNextOfKin.phone}</Text><br />
            <Text><strong>Relationship:</strong> {sampleNextOfKin.relationship}</Text>
          </Card>
        </div>

        <Divider />

        <Title level={3}>PDF Generation Options</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Click any of the buttons below to generate and download a populated loan agreement PDF:</Text>
          
          <Space>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleDirectDownload}
              size="large"
            >
              Direct Download
            </Button>
            
            <LoanAgreementGenerator
              loan={sampleLoan}
              borrower={sampleBorrower}
              nextOfKin={sampleNextOfKin}
              buttonProps={{
                type: "default",
                size: "large"
              }}
              showModal={false}
            />
            
            <LoanAgreementGenerator
              loan={sampleLoan}
              borrower={sampleBorrower}
              nextOfKin={sampleNextOfKin}
              buttonProps={{
                type: "dashed",
                size: "large"
              }}
              showModal={true}
            />
          </Space>
        </Space>

        <Divider />

        <Title level={4}>Features</Title>
        <ul>
          <li>✅ Automatically calculates all fees (4% interest, 6% processing, etc.)</li>
          <li>✅ Populates borrower and next of kin information</li>
          <li>✅ Includes loan terms and collateral details</li>
          <li>✅ Supports custom data entry via modal</li>
          <li>✅ Generates downloadable PDF file</li>
          <li>✅ Works with existing loan database structure</li>
        </ul>

        <Title level={4}>Integration Instructions</Title>
        <Text>
          The PDF generator has been added to your loan details page. You can also integrate it into other pages by importing the <code>LoanAgreementGenerator</code> component and passing loan/borrower data.
        </Text>
      </Card>
    </div>
  );
};

export default TestPDFGeneration;